import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { listStaff } from "../../api/staffApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listClassTeacherAssigns, assignClassTeacher, deleteClassTeacherAssign } from "../../api/classTeacherAssignApi";
import "../Attendance/Attendance.css";
import "../Users/UsersPage.css";

/**
 * Direct port of digicampusbd's `staff-assign-class-teacher` ("Teacher
 * Section Assign") — new `ClassTeacherAssign` entity/repository/service/
 * controller (a real net-new join table, `class_teacher_assign`, which
 * already existed in the real schema but had no JPA mapping — distinct from
 * the `shift_teacher_assign`/`ShiftTeacherAssign` added for the attendance-
 * reports category). Confirmed via `ClassTeacherAssignService
 * .assignTeacherToClasses` that despite the singular-sounding name this is
 * genuinely many-to-many: the old app's own unique constraint only prevents
 * assigning the SAME teacher to the SAME section twice — it never checks
 * whether a section already has a different class teacher — ported
 * faithfully, not "fixed" into a 1:1 model.
 */
export default function ClassTeacherAssignPage() {
  const [staffList, setStaffList] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [selectedConfigIds, setSelectedConfigIds] = useState([]);
  const [assigns, setAssigns] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadAssigns = () => listClassTeacherAssigns().then(setAssigns);

  useEffect(() => {
    listStaff().then((list) => {
      setStaffList(list);
      if (list.length > 0) setStaffId(String(list[0].staffId));
    });
    listClassConfigurations().then(setClassConfigs);
    loadAssigns();
  }, []);

  const toggleConfig = (id) => setSelectedConfigIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleAssign = async () => {
    setError("");
    setSuccess("");
    if (!staffId || selectedConfigIds.length === 0) {
      setError("Please select a staff member and at least one section.");
      return;
    }
    try {
      await assignClassTeacher(staffId, selectedConfigIds);
      setSuccess("Assigned.");
      setSelectedConfigIds([]);
      loadAssigns();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to assign.");
    }
  };

  const handleDelete = async (assignId) => {
    try {
      await deleteClassTeacherAssign([assignId]);
      loadAssigns();
    } catch (err) {
      setError("Failed to remove assignment.");
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Teacher Section Assign</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      {success && <p style={{ color: "var(--color-success, green)" }}>{success}</p>}
      <div className="attendance-filters">
        <label>
          Staff
          <select value={staffId} onChange={(e) => setStaffId(e.target.value)}>
            {staffList.map((s) => (
              <option key={s.staffId} value={s.staffId}>
                {s.staffName}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleAssign}>
          Assign
        </button>
      </div>
      <div className="form-row">
        <span className="admit-card-label">Sections:</span>{" "}
        {classConfigs.map((c) => (
          <label key={c.id} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
            <input type="checkbox" checked={selectedConfigIds.includes(String(c.id))} onChange={() => toggleConfig(String(c.id))} />
            {c.classConfigName}
          </label>
        ))}
      </div>
      <div className="table-scroll">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Staff</th>
              <th>Section</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {assigns.map((a) => (
              <tr key={a.assignId}>
                <td>{a.staffName}</td>
                <td>{a.classConfigName}</td>
                <td>
                  <button type="button" className="btn-link danger" onClick={() => handleDelete(a.assignId)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {assigns.length === 0 && (
              <tr>
                <td colSpan={3}>No class-teacher assignments yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </MainLayout>
  );
}
